import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, LinearProgress, ListItemIcon, ListItemText,
  Menu, MenuItem, Snackbar, Stack, Tab, Tabs, TextField, Tooltip, Typography,
  FormControl, InputLabel, Select,
} from '@mui/material';
import { Icon } from '@iconify/react';
import emailFill from '@iconify/icons-eva/email-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import paperPlaneFill from '@iconify/icons-eva/paper-plane-fill';
import checkmarkCircle from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircle from '@iconify/icons-eva/close-circle-fill';
import inboxFill from '@iconify/icons-eva/inbox-fill';
import searchFill from '@iconify/icons-eva/search-fill';
import closeOutline from '@iconify/icons-eva/close-outline';
import phoneCallFill from '@iconify/icons-eva/phone-call-fill';
import bellFill from '@iconify/icons-eva/bell-fill';
import briefcaseFill from '@iconify/icons-eva/briefcase-fill';
import flagFill from '@iconify/icons-eva/flag-fill';
import chevronDownFill from '@iconify/icons-eva/chevron-down-fill';
import chevronRightFill from '@iconify/icons-eva/chevron-right-fill';
import peopleFill from '@iconify/icons-eva/people-fill';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import Page from '../Page';
import StatusChip from '../Outreach/shared/StatusChip';
import UserInfo from 'src/utils/Authorization/UserInfo';
import { useNavigate } from 'react-router-dom';
import {
  getMyCompanies, getMyCompaniesStatusCounts, getMyCompanyDetail, getMyReplies, markReplyRead,
  replyToThread, getLinkedInThread, moveCompanyStage, listInvalidReasons, markCompanyInvalid,
  getCampaignEnrollmentsForPM, getCampaignEnrollmentStatusCounts, getMyReviewQueue,
  sendNowExecution, resumeEnrollment,
} from '../../utils/outreachApi';

// ─── helpers ──────────────────────────────────────────────────────────
const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return 'yesterday';
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const channelIcon = (channel, size = 14) => {
  const isLi = channel?.startsWith('LINKEDIN');
  return <Icon icon={isLi ? linkedinFill : emailFill} width={size}
               color={isLi ? '#0A66C2' : '#1976d2'} />;
};

const initials = (name) => (name || '')
  .split(' ').slice(0, 2).map((p) => p[0] || '').join('').toUpperCase() || '?';

// ─── LEFT PANE — company list, grouped by company with expandable contact rows ──

// Header row for a company group. Always rendered; clicking toggles expand.
// `summary` reflects aggregate state across all this company's enrollment rows
// (statuses, replies, latest activity time).
const CompanyGroupHeader = ({ company, contactCount, hasUnread, isExpanded,
                              summary, anyChildSelected, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      px: 1, py: 1.25, cursor: 'pointer',
      borderLeft: 3,
      borderLeftColor: anyChildSelected ? 'primary.main' : 'transparent',
      borderBottom: 1, borderBottomColor: 'divider',
      bgcolor: anyChildSelected ? 'primary.lighter' : 'background.paper',
      transition: 'background-color 0.1s',
      '&:hover': { bgcolor: anyChildSelected ? 'primary.lighter' : 'grey.100' },
    }}
  >
    <Stack direction="row" alignItems="flex-start" spacing={0.75}>
      <Icon icon={isExpanded ? chevronDownFill : chevronRightFill} width={18}
            style={{ marginTop: 8, color: '#666', flexShrink: 0 }} />
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar sx={{
          width: 34, height: 34, fontSize: 12, fontWeight: 700,
          bgcolor: hasUnread ? 'error.main' : 'grey.500',
        }}>
          {initials(company.company_name)}
        </Avatar>
        {hasUnread && (
          <Box sx={{
            position: 'absolute', top: -2, right: -2,
            width: 11, height: 11, borderRadius: '50%',
            bgcolor: 'error.main', border: '2px solid white',
          }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: hasUnread ? 700 : 600, flex: 1 }} noWrap>
            {company.company_name}
          </Typography>
          {summary?.lastActivityAt && (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {relativeTime(summary.lastActivityAt)}
            </Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {[company.industry, company.country].filter(Boolean).join(' · ')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            icon={<Icon icon={peopleFill} width={11} />}
            label={contactCount === 1 ? '1 contact' : `${contactCount} contacts`}
            sx={{ height: 18, fontSize: 10, '& .MuiChip-icon': { ml: 0.5 } }}
          />
          {summary?.hasEmailReply    && <Icon icon={emailFill}    width={12} color="#1976d2" />}
          {summary?.hasLinkedinReply && <Icon icon={linkedinFill} width={12} color="#0A66C2" />}
        </Stack>
      </Box>
    </Stack>
  </Box>
);

// One contact (enrollment) under a company group.
const ContactRow = ({ enrollment, hasUnread, isSelected, onClick }) => {
  const personName = enrollment.primary_member_name || '(unknown contact)';
  const personEmail = enrollment.primary_member_email || enrollment.recipient_email_snapshot || '';
  return (
    <Box
      onClick={onClick}
      sx={{
        pl: 5, pr: 1.5, py: 1, cursor: 'pointer',
        borderLeft: 3,
        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
        borderBottom: 1, borderBottomColor: 'divider',
        bgcolor: isSelected ? 'primary.lighter' : 'background.paper',
        '&:hover': { bgcolor: isSelected ? 'primary.lighter' : 'grey.50' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: hasUnread ? 700 : 500 }} noWrap>
            {personName}
          </Typography>
          {personEmail && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {personEmail}
            </Typography>
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <StatusChip status={enrollment.status} sx={{ height: 18, fontSize: 10 }} />
          {enrollment.has_email_reply    && <Icon icon={emailFill}    width={11} color="#1976d2" />}
          {enrollment.has_linkedin_reply && <Icon icon={linkedinFill} width={11} color="#0A66C2" />}
        </Stack>
      </Stack>
    </Box>
  );
};

// Kept for the (now legacy) flat layout, in case anything else imports it.
const CompanyListItem = ({ company, hasUnread, isSelected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2, py: 1.5, cursor: 'pointer',
      borderLeft: 3,
      borderLeftColor: isSelected ? 'primary.main' : 'transparent',
      borderBottom: 1, borderBottomColor: 'divider',
      bgcolor: isSelected ? 'primary.lighter' : 'background.paper',
      transition: 'background-color 0.1s',
      '&:hover': { bgcolor: isSelected ? 'primary.lighter' : 'grey.100' },
    }}
  >
    <Stack direction="row" alignItems="flex-start" spacing={1.25}>
      <Box sx={{ position: 'relative' }}>
        <Avatar sx={{
          width: 36, height: 36, fontSize: 13, fontWeight: 700,
          bgcolor: hasUnread ? 'error.main' : 'grey.400',
        }}>
          {initials(company.company_name)}
        </Avatar>
        {hasUnread && (
          <Box sx={{
            position: 'absolute', top: -2, right: -2,
            width: 12, height: 12, borderRadius: '50%',
            bgcolor: 'error.main', border: '2px solid white',
            animation: 'pulse 1.3s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%':      { opacity: 0.45, transform: 'scale(0.85)' },
            },
          }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" sx={{
            fontWeight: hasUnread ? 700 : 600, flex: 1,
          }} noWrap>
            {company.company_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {relativeTime(company.last_activity_at)}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {company.industry} · {company.country}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <StatusChip status={company.status} sx={{ height: 18, fontSize: 10 }} />
          {(company.has_email_reply || company.has_linkedin_reply) && (
            <Stack direction="row" spacing={0.25}>
              {company.has_email_reply    && <Icon icon={emailFill}    width={12} color="#1976d2" />}
              {company.has_linkedin_reply && <Icon icon={linkedinFill} width={12} color="#0A66C2" />}
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  </Box>
);

const STATUS_FILTERS = [
  { k: 'all',     label: 'All',     match: () => true },
  { k: 'replied', label: 'Replied', match: (c) => c.status === 'STOPPED_REPLIED' },
  { k: 'active',  label: 'Active',  match: (c) => c.status === 'ACTIVE' },
  { k: 'invalid', label: 'Invalid', match: (c) => c.status === 'INVALID' },
  { k: 'done',    label: 'Done',    match: (c) => c.status === 'COMPLETED' },
];

const CompanyListPane = ({
  companies, replies, selectedId, onSelect, title,
  // Server-driven filter state + counts (lifted to MyOutreachHome).
  statusKey, onStatusKeyChange, counts,
  // Server pagination — total reflects current filter on the server.
  totalForCurrentFilter, hasMore, loadingMore, onLoadMore,
  // When true (non-campaign mode), filter chips run client-side and counts
  // are computed from the loaded array.
  clientSideFilter = false,
}) => {
  const [search, setSearch] = useState('');
  // Accordion behavior: at most one group expanded at a time.
  // null = everything collapsed; otherwise the one company_id that's open.
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);

  // When the selected enrollment changes, make its company group the open one.
  useEffect(() => {
    if (!selectedId || !companies) return;
    const selectedEnr = companies.find((c) => c.id === selectedId);
    if (!selectedEnr) return;
    const companyId = selectedEnr.company_id ?? selectedEnr.company ?? selectedEnr.company_name;
    setExpandedCompanyId(companyId);
  }, [selectedId, companies]);

  // If client-side filtering is on, apply the status matcher locally and
  // override counts from the loaded array.
  let workingCompanies = companies;
  let effectiveCounts = counts;
  if (clientSideFilter) {
    const matcher = STATUS_FILTERS.find((f) => f.k === statusKey)?.match || (() => true);
    workingCompanies = companies.filter(matcher);
    effectiveCounts = STATUS_FILTERS.reduce((acc, f) => {
      acc[f.k] = companies.filter(f.match).length;
      return acc;
    }, {});
  }

  // Search matches BOTH company-level fields and the primary contact's name/email,
  // so BAs can search by either dimension. Runs against the loaded subset.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? workingCompanies.filter((c) => {
        const haystack = [
          c.company_name, c.company_domain, c.industry, c.country,
          c.primary_member_name, c.primary_member_email,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      })
    : workingCompanies;

  // Group filtered enrollments by company_id. The first row per company donates
  // its company metadata to the group header.
  const groups = (() => {
    const byCompany = new Map();
    for (const enr of filtered) {
      const key = enr.company_id ?? enr.company ?? enr.company_name;
      if (!byCompany.has(key)) byCompany.set(key, { company: enr, rows: [] });
      byCompany.get(key).rows.push(enr);
    }
    return Array.from(byCompany.values());
  })();

  // When searching, auto-expand every match. Otherwise only the one currently-open
  // group is expanded — clicking another collapses the previous one (accordion).
  const isExpanded = (companyId) => (q ? true : expandedCompanyId === companyId);
  const toggleCompany = (companyId) => {
    setExpandedCompanyId((prev) => (prev === companyId ? null : companyId));
  };

  const unreadCount = companies.filter((c) =>
    replies.some((r) => r.enrollment === c.id && r.is_unread)
  ).length;

  // Total displayed reflects the count for the active chip:
  //  - server-driven mode → comes from the API (full count, paginated below)
  //  - client-side mode   → matches the locally-filtered array
  const total = clientSideFilter
    ? workingCompanies.length
    : (typeof totalForCurrentFilter === 'number' ? totalForCurrentFilter : companies.length);

  // Infinite-scroll trigger — near bottom of list.
  const handleScroll = (e) => {
    if (!hasMore || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 240) onLoadMore?.();
  };

  return (
    <Box sx={{
      width: 320, flexShrink: 0,
      borderRight: 1, borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {title || 'My Outreach'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filtered.length === total
                ? `${total} compan${total === 1 ? 'y' : 'ies'}`
                : `${filtered.length} of ${total}`}
              {unreadCount > 0 && (
                <>
                  {' · '}
                  <Typography component="span" variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                    {unreadCount} need reply
                  </Typography>
                </>
              )}
            </Typography>
          </Box>
        </Stack>

        <TextField
          fullWidth size="small"
          placeholder="Search company or person…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <Icon icon={searchFill} width={16} style={{ marginRight: 6, color: '#999' }} />
            ),
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch('')}>
                <Icon icon={closeOutline} width={14} />
              </IconButton>
            ) : null,
          }}
          sx={{ mb: 1.25 }}
        />

        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
          {STATUS_FILTERS.map((f) => (
            <Chip
              key={f.k}
              size="small"
              clickable
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>{f.label}</span>
                  {effectiveCounts?.[f.k] !== undefined && (
                    <Typography component="span" variant="caption" sx={{ opacity: 0.7 }}>
                      {effectiveCounts[f.k]}
                    </Typography>
                  )}
                </Stack>
              }
              color={statusKey === f.k ? 'primary' : 'default'}
              variant={statusKey === f.k ? 'filled' : 'outlined'}
              onClick={() => onStatusKeyChange?.(f.k)}
              sx={{ height: 22 }}
            />
          ))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }} onScroll={handleScroll}>
        {groups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {total === 0
                ? 'No companies in this view.'
                : q
                ? 'No matches in loaded rows. Scroll to load more, or change filter.'
                : 'No companies in this view.'}
            </Typography>
          </Box>
        ) : (
          <>
            {groups.map(({ company, rows }) => {
              const companyId = company.company_id ?? company.company ?? company.company_name;
              const expanded  = isExpanded(companyId);
              // Aggregate state across all enrollment rows in this group.
              const groupHasUnread = rows.some((r) =>
                replies.some((rep) => rep.enrollment === r.id && rep.is_unread)
              );
              const hasEmailReply    = rows.some((r) => r.has_email_reply);
              const hasLinkedinReply = rows.some((r) => r.has_linkedin_reply);
              const lastActivityAt = rows
                .map((r) => r.last_activity_at)
                .filter(Boolean)
                .sort()
                .pop();
              const anyChildSelected = rows.some((r) => r.id === selectedId);

              // 1-contact group: header click toggles expand AND selects the only
              // enrollment when opening — preserving the original 1-click flow.
              // Multi-contact: toggle expand only.
              const handleHeaderClick = () => {
                if (rows.length === 1) {
                  if (expandedCompanyId === companyId) {
                    setExpandedCompanyId(null);     // re-click closes
                  } else {
                    setExpandedCompanyId(companyId); // accordion: replace open one
                    onSelect(rows[0].id);
                  }
                } else {
                  toggleCompany(companyId);
                }
              };

              return (
                <React.Fragment key={companyId}>
                  <CompanyGroupHeader
                    company={company}
                    contactCount={rows.length}
                    hasUnread={groupHasUnread}
                    isExpanded={expanded}
                    summary={{ hasEmailReply, hasLinkedinReply, lastActivityAt }}
                    anyChildSelected={anyChildSelected}
                    onToggle={handleHeaderClick} />
                  {expanded && rows.map((enr) => {
                    const rowHasUnread = replies.some((rep) =>
                      rep.enrollment === enr.id && rep.is_unread
                    );
                    return (
                      <ContactRow
                        key={enr.id}
                        enrollment={enr}
                        hasUnread={rowHasUnread}
                        isSelected={selectedId === enr.id}
                        onClick={() => onSelect(enr.id)} />
                    );
                  })}
                </React.Fragment>
              );
            })}
            {loadingMore && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={18} />
              </Box>
            )}
            {!hasMore && companies.length > 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  End of list · {groups.length} compan{groups.length === 1 ? 'y' : 'ies'} loaded
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

// ─── CENTER PANE — selected company's details ─────────────────────────
const operatorLabel = (op) => ({
  eq: 'equals', in: 'in', gte: '≥', lte: '≤', between: 'between', isnotnull: 'is set',
}[op] || op);
const formatValue = (v) => Array.isArray(v) ? v.join(', ') : String(v);

// Format a raw revenue integer like 12_500_000 → "$12.5M"
const formatRevenue = (n) => {
  if (n == null) return null;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const stripProtocol = (url) => (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

const DetailRow = ({ label, value, href }) => {
  if (value == null || value === '') return null;
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 0.5 }}>
      <Typography variant="caption"
                  sx={{ minWidth: 78, color: 'text.secondary', textTransform: 'uppercase',
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.6 }}>
        {label}
      </Typography>
      {href ? (
        <Typography component="a" href={href} target="_blank" rel="noopener noreferrer"
                    variant="body2"
                    sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all',
                          '&:hover': { textDecoration: 'underline' } }}>
          {value}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
      )}
    </Stack>
  );
};

const CompanyDetailsBlock = ({ enrollment }) => {
  const e = enrollment;
  const pm = e?.primary_member || null;
  const websiteHref = e?.company_domain
    ? (e.company_domain.startsWith('http') ? e.company_domain : `https://${e.company_domain}`)
    : null;

  return (
    <Card variant="outlined" sx={{ p: 1.75 }}>
      <Typography variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
        COMPANY DETAILS
      </Typography>
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        <DetailRow label="Industry" value={e?.industry} />
        <DetailRow label="Country"  value={e?.country} />
        <DetailRow label="Employees" value={e?.fte != null ? e.fte.toLocaleString() : null} />
        <DetailRow label="Revenue"  value={formatRevenue(e?.revenue)} />
        <DetailRow label="Founded"  value={e?.foundation_year} />
        <DetailRow label="Website"  value={stripProtocol(e?.company_domain)} href={websiteHref} />
      </Stack>

      {e?.business_description && (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption"
                      sx={{ color: 'text.secondary', textTransform: 'uppercase',
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.6 }}>
            About
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', lineHeight: 1.55 }}>
            {e.business_description}
          </Typography>
        </Box>
      )}

      {pm && (pm.name || pm.email || pm.linkedin) && (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption"
                      sx={{ color: 'text.secondary', textTransform: 'uppercase',
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
            Primary contact
          </Typography>
          <Stack spacing={0.25}>
            {pm.name && <Typography variant="body2" sx={{ fontWeight: 600 }}>{pm.name}</Typography>}
            {pm.email && (
              <Typography component="a" href={`mailto:${pm.email}`} variant="body2"
                          sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all',
                                '&:hover': { textDecoration: 'underline' } }}>
                {pm.email}
              </Typography>
            )}
            {pm.linkedin && (
              <Typography component="a" href={pm.linkedin} target="_blank" rel="noopener noreferrer"
                          variant="body2"
                          sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all',
                                '&:hover': { textDecoration: 'underline' } }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Icon icon={linkedinFill} width={14} />
                  <span>{stripProtocol(pm.linkedin)}</span>
                </Stack>
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Card>
  );
};

const SequenceBlock = ({ campaign, executions, onSendNow }) => {
  const steps = campaign?.steps || [];
  const execByOrder = {};
  executions.forEach((e) => { execByOrder[e.step_order] = e; });
  const [firing, setFiring] = useState(null);

  const fireNow = async (execId) => {
    if (firing) return;
    setFiring(execId);
    try {
      await sendNowExecution(execId);
      onSendNow?.();
    } finally {
      // Detail will reload; clear flag a moment later to give the worker time
      setTimeout(() => setFiring(null), 1500);
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 1.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
        SEQUENCE
      </Typography>
      {steps.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No steps.</Typography>
      ) : (
        <Stack spacing={0.75}>
          {steps.map((s) => {
            const exec = execByOrder[s.order];
            const isLi = s.channel?.startsWith('LINKEDIN');
            const canFireNow = exec
              && (exec.status === 'SCHEDULED' || exec.status === 'PENDING');
            return (
              <Stack key={s.order} direction="row" alignItems="center" spacing={1.25}
                     sx={{ p: 0.75, borderRadius: 1, bgcolor: 'grey.50' }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: '50%',
                  bgcolor: 'primary.lighter', color: 'primary.dark',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                }}>
                  {s.order}
                </Box>
                <Icon icon={isLi ? linkedinFill : emailFill} width={13}
                      color={isLi ? '#0A66C2' : '#1976d2'} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {s.channel.replace('_', ' ').toLowerCase()}
                </Typography>
                {exec ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {exec.sent_at ? `sent ${relativeTime(exec.sent_at)}`
                        : exec.scheduled_at ? `→ ${relativeTime(exec.scheduled_at)}` : '—'}
                    </Typography>
                    <StatusChip status={exec.status} sx={{ height: 18, fontSize: 10 }} />
                    {canFireNow && (
                      <Button
                        size="small"
                        variant="contained"
                        color={isLi ? 'primary' : 'inherit'}
                        disabled={firing === exec.id}
                        onClick={() => fireNow(exec.id)}
                        sx={{ height: 22, minWidth: 0, px: 1, fontSize: 10, textTransform: 'none' }}
                      >
                        {firing === exec.id ? '…' : 'Send now'}
                      </Button>
                    )}
                  </>
                ) : (
                  <Chip size="small" label="not started" sx={{ height: 18, fontSize: 10 }} />
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Card>
  );
};

const DETAILS_PANE_W = 420;

// ─── Stage move menu ─────────────────────────────────────────────────
const STAGE_OPTIONS = [
  { k: 'intro call',      label: 'Intro call',                icon: phoneCallFill, color: '#2e7d32' },
  { k: 'future-outreach', label: 'Future outreach',           icon: bellFill,      color: '#7e57c2', supports: ['newsletter_only'] },
  { k: 'workshop + dm',   label: 'Workshop + DM',             icon: briefcaseFill, color: '#0288d1' },
];
const MORE_STAGES = [
  { k: 'nb loi + msa',  label: 'NB LOI + MSA' },
  { k: 'dd - phase',    label: 'DD — phase' },
  { k: 'binding loi',   label: 'Binding LOI' },
  { k: 'psa',           label: 'PSA' },
  { k: 'closing',       label: 'Closing' },
];

const stageLabelFor = (k) => {
  const o = STAGE_OPTIONS.find((s) => s.k === k) || MORE_STAGES.find((s) => s.k === k);
  if (o) return o.label;
  if (k === 'not_interested') return 'Not interested';
  return null;
};

// ─── Mark Invalid Dialog — mirrors /dashboard's pattern ─────────────
const MarkInvalidDialog = ({ open, company, busy, onClose, onSubmit }) => {
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setReasonId(''); setDescription('');
    listInvalidReasons().then(({ data }) => setReasons(data));
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{
        bgcolor: 'warning.main', color: 'warning.contrastText',
        px: 2.5, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon icon={closeCircle} width={20} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Mark as Invalid</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <Icon icon={closeCircle} width={20} />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ p: 1.5, mb: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Company</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {company?.company_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {company?.company_domain} · {company?.industry} · {company?.country}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          Reason *
        </Typography>
        <Select fullWidth size="small" displayEmpty
                value={reasonId}
                onChange={(e) => setReasonId(e.target.value)}
                sx={{ mb: 2 }}>
          <MenuItem value="" disabled>Select a reason</MenuItem>
          {reasons.map((r) => (
            <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
          ))}
        </Select>

        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          Additional details (optional)
        </Typography>
        <TextField
          fullWidth multiline minRows={3}
          placeholder="Add context — e.g. 'Replied saying they're already a customer'"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ p: 1.25, bgcolor: 'warning.lighter', border: 1, borderColor: 'warning.main', borderRadius: 1 }}>
          <Typography variant="caption" color="warning.dark">
            ⚠ This stops the outreach automation for this company and records it as invalid.
            It also prevents it from being re-enrolled by this or other campaigns.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="warning"
                disabled={busy || !reasonId}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={() => onSubmit({ reason_id: reasonId, description })}>
          {busy ? 'Saving…' : 'Mark as Invalid'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StageSection = ({ enrollment, busy, onMove, onMarkInvalid }) => {
  const [anchor, setAnchor] = useState(null);
  const [confirmStage, setConfirmStage] = useState(null);   // when picking newsletter-only
  const [moreOpen, setMoreOpen] = useState(false);
  const [invalidOpen, setInvalidOpen] = useState(false);
  const [newsletterOnly, setNewsletterOnly] = useState(false);
  const moved = enrollment.moved_to_stage;
  const movedLabel = moved && stageLabelFor(moved);
  const isInvalid = enrollment.status === 'INVALID';

  const handlePick = (opt) => {
    setAnchor(null);
    if (opt.supports?.includes('newsletter_only')) {
      setConfirmStage(opt);
      setNewsletterOnly(true);
      return;
    }
    onMove(opt.k);
  };

  const handleMore = (k) => {
    setMoreOpen(false);
    onMove(k);
  };

  return (
    <Card variant="outlined" sx={{ p: 1.75 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>STAGE</Typography>
        <Button size="small" variant="outlined"
                endIcon={<Icon icon={chevronDownFill} width={14} />}
                onClick={(e) => setAnchor(e.currentTarget)}>
          Move to…
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
        <Chip
          size="small"
          icon={<Icon icon={moved ? checkmarkCircle : flagFill} width={13} />}
          label={movedLabel || 'Outreach (default)'}
          color={moved ? 'success' : 'default'}
          variant={moved ? 'filled' : 'outlined'}
        />
        {enrollment.stage_options?.newsletter_only && (
          <Chip size="small" variant="outlined" icon={<Icon icon={bellFill} width={12} />}
                label="newsletter only" />
        )}
        {enrollment.stage_moved_at && (
          <Typography variant="caption" color="text.secondary">
            · moved {relativeTime(enrollment.stage_moved_at)}
          </Typography>
        )}
      </Stack>

      {/* Show invalid-reason context if marked invalid */}
      {isInvalid && enrollment.invalid_reason_label && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'error.lighter', borderRadius: 1,
                   border: 1, borderColor: 'error.light' }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'error.dark' }}>
            ⚠ {enrollment.invalid_reason_label}
          </Typography>
          {enrollment.invalid_description && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'error.dark' }}>
              {enrollment.invalid_description}
            </Typography>
          )}
        </Box>
      )}

      {/* Quick-pick menu */}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {STAGE_OPTIONS.map((o) => (
          <MenuItem key={o.k} onClick={() => handlePick(o)}>
            <ListItemIcon><Icon icon={o.icon} color={o.color} width={16} /></ListItemIcon>
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); setInvalidOpen(true); }}>
          <ListItemIcon><Icon icon={closeCircle} color="#ed6c02" width={16} /></ListItemIcon>
          <ListItemText primary="Mark as Invalid…" />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setMoreOpen(true); }}>
          <ListItemIcon><Icon icon={chevronDownFill} width={16} /></ListItemIcon>
          <ListItemText primary="More stages…" />
        </MenuItem>
      </Menu>

      {/* Mark as Invalid dialog */}
      <MarkInvalidDialog
        open={invalidOpen}
        company={enrollment}
        busy={busy}
        onClose={() => setInvalidOpen(false)}
        onSubmit={async (payload) => {
          await onMarkInvalid(payload);
          setInvalidOpen(false);
        }} />

      {/* Newsletter-only confirm dialog */}
      <Dialog open={!!confirmStage} onClose={() => setConfirmStage(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Move to {confirmStage?.label}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            This stops the outreach automation for this company.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={newsletterOnly}
                               onChange={(e) => setNewsletterOnly(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Newsletter-only
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Keep this company on the CRM newsletter list, but no further direct outreach.
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStage(null)}>Cancel</Button>
          <Button variant="contained"
                  onClick={() => {
                    onMove(confirmStage.k, { newsletter_only: newsletterOnly });
                    setConfirmStage(null);
                  }}>
            Move company
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full stage list */}
      <Dialog open={moreOpen} onClose={() => setMoreOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move to a stage</DialogTitle>
        <DialogContent>
          <Stack spacing={0.5}>
            {[...STAGE_OPTIONS, ...MORE_STAGES].map((s) => (
              <Button key={s.k} variant="outlined" fullWidth
                      onClick={() => handleMore(s.k)}
                      sx={{ justifyContent: 'flex-start' }}>
                {s.label}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoreOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const DetailsPane = ({ detail, isLoading, busy, onMoveStage, onMarkInvalid, onSendNow }) => {
  const wrapperSx = {
    width: DETAILS_PANE_W, flexShrink: 0,
    borderLeft: 1, borderColor: 'divider',
    bgcolor: 'background.paper',
    height: '100%',
  };
  if (isLoading) return (
    <Box sx={{ ...wrapperSx, p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
  if (!detail) return (
    <Box sx={{ ...wrapperSx, display: 'flex', flexDirection: 'column',
               alignItems: 'center', justifyContent: 'center', p: 4 }}>
      <Avatar sx={{ width: 64, height: 64, bgcolor: 'grey.100', color: 'grey.500', mb: 2 }}>
        <Icon icon={inboxFill} width={32} />
      </Avatar>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Pick a company on the left
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
        Replies show in the center,<br/>details show here.
      </Typography>
    </Box>
  );

   const e = detail;
   return (
     <Box sx={{
       flex: 1, minWidth: 0,
       bgcolor: 'background.paper',
       display: 'flex', flexDirection: 'column', height: '100%',
     }}>
       {/* Company identity header */}
       <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
         <Stack direction="row" alignItems="center" spacing={1.25}>
           <Avatar sx={{
             width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: 'primary.main',
           }}>
             {initials(e.company_name)}
           </Avatar>
           <Box sx={{ flex: 1, minWidth: 0 }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
               {e.company_name}
             </Typography>
             <Stack direction="row" alignItems="center" spacing={0.5}>
               <StatusChip status={e.status} sx={{ height: 18, fontSize: 10 }} />
               {e.campaign?.name && (
                 <Typography variant="caption" color="text.secondary" noWrap>
                   · {e.campaign.name}
                 </Typography>
               )}
             </Stack>
           </Box>
         </Stack>
       </Box>

       <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {e.last_error && (
          <Alert severity="error" sx={{ mb: 2 }}>{e.last_error}</Alert>
        )}
        <Stack spacing={1.5}>
          <StageSection enrollment={e} busy={busy}
                        onMove={(stage, opts) => onMoveStage(e.id, stage, opts)}
                        onMarkInvalid={(payload) => onMarkInvalid(e.id, payload)} />
          <CompanyDetailsBlock enrollment={e} />
          <SequenceBlock campaign={detail.campaign} executions={detail.executions} onSendNow={onSendNow} />
        </Stack>
      </Box>
    </Box>
  );
};

// Split email HTML into new content only; strips quoted chains.
// Handles: <hr id="previousmessagehr"> (Outlook Web App / Exchange),
//   <blockquote>, #divRplyFwdMsg, .gmail_quote, bare <hr>, and
//   plain-text "From: Sent: To:" header blocks.
//
// Strategy: find the separator in a DOM clone; remove it + following siblings
// in-place (propagate up while parent becomes empty). Works correctly even
// when the separator is nested inside a font-wrapper div.
const splitQuotedHtml = (html) => {
  if (!html) return { main: html, hasQuoted: false };

  const SELECTORS = [
    'blockquote',
    '#previousmessagehr',
    'hr[id]',
    '#divRplyFwdMsg',
    '[id*="divRplyFwdMsg"]',
    '.gmail_quote',
    'hr',
  ];

  const findSplit = (root) => {
    for (const sel of SELECTORS) {
      const el = root.querySelector(sel);
      if (el) return el;
    }
    // Fallback: plain-text Outlook "From: Sent: To:" block
    for (const el of root.querySelectorAll('div,p,td')) {
      const t = el.textContent?.trim() ?? '';
      if (/^from:\s/i.test(t) && /sent:/i.test(t) && /to:/i.test(t)) return el;
    }
    return null;
  };

  try {
    // Quick probe — avoid cloning if there is no split point
    const probe = document.createElement('div');
    probe.innerHTML = html;
    if (!findSplit(probe)) return { main: html, hasQuoted: false };

    // Clone and surgically prune quoted content
    const clone = document.createElement('div');
    clone.innerHTML = html;
    let node = findSplit(clone);

    while (node && node !== clone) {
      const parent = node.parentNode;
      if (!parent) break;
      // Remove node + all following siblings
      let cur = node;
      while (cur) { const nx = cur.nextSibling; parent.removeChild(cur); cur = nx; }
      if (parent === clone) break;
      // Propagate up while parent is now empty of visible text
      if (!(parent.textContent?.trim())) node = parent;
      else break;
    }

    const mainHtml = clone.innerHTML.trim();
    return { main: mainHtml || html, hasQuoted: mainHtml.length < html.length - 20 };
  } catch (_) {
    return { main: html, hasQuoted: false };
  }
};


const EmailIframe = ({ html }) => {
  const ref = useRef(null);
  const onLoad = () => {
    try {
      const doc = ref.current?.contentDocument;
      if (doc) {
        const h = Math.min(
          doc.documentElement.scrollHeight || doc.body?.scrollHeight || 200,
          600
        );
        ref.current.style.height = h + 'px';
      }
    } catch (_) { /* cross-origin resize blocked */ }
  };
  if (!html) return <Typography variant="caption" color="text.secondary">(no body)</Typography>;
  return (
    <iframe
      ref={ref}
      srcDoc={html}
      title="email"
      sandbox="allow-popups allow-same-origin"
      style={{ width: '100%', border: 'none', minHeight: 80, display: 'block' }}
      onLoad={onLoad}
    />
  );
};

// Single email message card — shows new content only; collapses quoted chain
const EmailMessageCard = ({ msg }) => {
  const [showFull, setShowFull] = useState(false);
  const { main, hasQuoted } = useMemo(() => splitQuotedHtml(msg.body_html), [msg.body_html]);
  const displayHtml = showFull ? msg.body_html : main;

  const ts = msg.timestamp
    ? new Date(msg.timestamp).toLocaleString([], {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <Box sx={{
      borderRadius: 1.5, border: 1, overflow: 'hidden',
      borderColor: msg.is_outbound ? 'primary.light' : 'divider',
      bgcolor: msg.is_outbound ? '#f0f7ff' : 'background.paper',
    }}>
      {/* Header row */}
      <Stack direction="row" alignItems="center" spacing={1}
             sx={{ px: 1.5, py: 0.75, bgcolor: msg.is_outbound ? '#dbeeff' : 'grey.100' }}>
        <Avatar sx={{
          width: 26, height: 26, fontSize: 10, fontWeight: 700,
          bgcolor: msg.is_outbound ? 'primary.main' : 'grey.600',
        }}>
          {initials(msg.sender_name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {msg.sender_name}
            </Typography>
            {msg.is_outbound && (
              <Chip size="small" label="Sent"
                    sx={{ height: 16, fontSize: 9, '.MuiChip-label': { px: 0.5 },
                          bgcolor: 'primary.lighter', color: 'primary.dark' }} />
            )}
          </Stack>
          {msg.sender_email && (
            <Typography variant="caption" color="text.secondary" noWrap
                        sx={{ display: 'block', maxWidth: 260, fontSize: 10 }}>
              {msg.sender_email}
            </Typography>
          )}
          {msg.subject && (
            <Typography variant="caption" color="text.secondary" noWrap
                        sx={{ display: 'block', maxWidth: 260 }}>
              {msg.subject}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: 11 }}>
          {ts}
        </Typography>
      </Stack>

      {/* Body */}
      <Box sx={{ px: 1.5, pt: 1, pb: hasQuoted ? 0.5 : 1 }}>
        <EmailIframe html={displayHtml} />
        {hasQuoted && (
          <Button size="small" onClick={() => setShowFull((p) => !p)}
                  sx={{
                    mt: 0.25, mb: 0.5, fontSize: 11, color: 'text.secondary',
                    textTransform: 'none', minHeight: 0, py: 0.25, px: 0.75,
                    border: 1, borderColor: 'divider', borderRadius: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}>
            {showFull ? '▲ Hide quoted' : '··· Show quoted'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// ─── CENTER PANE — replies with Email / LinkedIn tabs + inline composer
const ChatBubble = ({ msg }) => {
  const out = msg.is_outbound;
  return (
    <Stack direction="row" justifyContent={out ? 'flex-end' : 'flex-start'} sx={{ mb: 1 }}>
      <Box sx={{
        maxWidth: '72%',
        px: 1.5, py: 1,
        borderRadius: out ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        bgcolor: out ? '#0A66C2' : 'grey.100',
        color: out ? 'white' : 'text.primary',
      }}>
        {!out && msg.sender_name && (
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25, opacity: 0.7 }}>
            {msg.sender_name}
          </Typography>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {msg.text || '(no content)'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6, fontSize: 10 }}>
          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
        </Typography>
      </Box>
    </Stack>
  );
};

const LinkedInChatView = ({ reply, busy, onSend }) => {
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState('');
  const bottomRef = useRef(null);
  const lastCountRef = useRef(0);

  const loadThread = (silent = false) => {
    if (!silent) setLoadingThread(true);
    getLinkedInThread(reply.id)
      .then((res) => {
        const msgs = res.data.messages || [];
        setMessages(msgs);
        setThreadError('');
        lastCountRef.current = msgs.length;
      })
      .catch((err) => {
        setMessages((p) => p || []);
        setThreadError(err?.response?.data?.detail || 'Could not load LinkedIn conversation.');
      })
      .finally(() => { if (!silent) setLoadingThread(false); });
  };

  // Initial load
  useEffect(() => { loadThread(); }, [reply.id]);

  // Poll every 5 s for new inbound messages
  useEffect(() => {
    const id = setInterval(() => loadThread(true), 5000);
    return () => clearInterval(id);
  }, [reply.id]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    await onSend(reply.id, text, { takeOver: false });
    // Optimistically add our message then reload
    setMessages((prev) => [...(prev || []), {
      id: Date.now(), text, is_outbound: true, sender_name: 'You',
      timestamp: new Date().toISOString(),
    }]);
    // Poll will sync within 5 s; do a quicker silent reload after 2 s to confirm
    setTimeout(() => loadThread(true), 2000);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Icon icon={linkedinFill} color="#0A66C2" width={16} />
        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
          {reply.member?.name || 'LinkedIn conversation'}
        </Typography>
        <Chip size="small" label="Sequence stopped" color="default" sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      {/* Messages area */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {loadingThread && !messages && <LinearProgress sx={{ mb: 1 }} />}
        {threadError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>{threadError}</Alert>
        )}
        {!threadError && messages && messages.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            No messages loaded
          </Typography>
        )}
        {(messages || []).map((m) => <ChatBubble key={m.id} msg={m} />)}
        <div ref={bottomRef} />
      </Box>

      {/* Composer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth multiline maxRows={4}
            placeholder={`Message ${(reply.member?.name || '').split(' ')[0] || ''}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            disabled={busy}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <IconButton
            color="primary"
            disabled={busy || !draft.trim()}
            onClick={handleSend}
            sx={{ bgcolor: '#0A66C2', color: 'white', '&:hover': { bgcolor: '#004182' },
                  '&.Mui-disabled': { bgcolor: 'grey.200' }, flexShrink: 0 }}>
            <Icon icon={paperPlaneFill} width={18} />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Press Enter to send · Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Threaded email conversation (outbound + inbound + portal replies)
const EmailThreadView = ({ detail, emailReplies, busy, onSend }) => {
  const [draft, setDraft] = useState('');
  const rawThread = detail?.email_thread;
  const thread = (rawThread && rawThread.length > 0)
    ? rawThread
    : emailReplies.map((r) => ({
        is_outbound: false,
        sender_name: r.from_name || r.from_email || 'Prospect',
        subject:     r.subject || '',
        body_html:   r.body_html || r.body_preview || '',
        timestamp:   r.received_at,
        reply_id:    r.id,
      }));
  const latestReply = emailReplies[emailReplies.length - 1] || null;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {thread.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 1, opacity: 0.4 }}><Icon icon={emailFill} width={32} /></Box>
          <Typography variant="caption" color="text.secondary">No email activity yet.</Typography>
        </Box>
      ) : thread.map((msg, i) => (
        <EmailMessageCard key={i} msg={msg} />
      ))}

      {/* Reply composer */}
      {latestReply && (
        <Box sx={{ mt: 0.5, p: 1.75, border: 1, borderColor: 'divider', borderRadius: 1.5,
                   bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <Icon icon={emailFill} color="#1976d2" width={14} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Reply via Email · from your Outlook
            </Typography>
          </Stack>
          <TextField fullWidth multiline minRows={3}
            placeholder={`Reply to ${latestReply.member?.name || ''}…`}
            value={draft} onChange={(e) => setDraft(e.target.value)} disabled={busy}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }} />
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button size="small" variant="contained" disabled={busy || !draft.trim()}
                    startIcon={<Icon icon={paperPlaneFill} width={14} />}
                    onClick={() => { onSend(latestReply.id, draft.trim(), { takeOver: false }); setDraft(''); }}>
              Send reply
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const ReplyView = ({ reply, busy, onSend }) => {
  const isLi = reply.channel?.startsWith('LINKEDIN');
  if (isLi) return <LinkedInChatView reply={reply} busy={busy} onSend={onSend} />;
  return null; // email handled by EmailThreadView
};

const RepliesPane = ({ detail, busy, onSend, onResume }) => {
  const [tab, setTab] = useState(0);
  const [resumeStage, setResumeStage] = useState('idle'); // idle → first → second → working
  const [resuming, setResuming] = useState(false);

  // Reset the two-step confirm whenever the selected enrollment changes.
  useEffect(() => { setResumeStage('idle'); }, [detail?.id]);

  const handleResume = async () => {
    if (resuming) return;
    setResuming(true);
    try {
      await onResume?.(detail.id);
    } finally {
      setResuming(false);
      setResumeStage('idle');
    }
  };

  // Derive replies by channel from the flat replies list
  const emailReplies = useMemo(() =>
    (detail?.replies || []).filter((r) => r.channel === 'EMAIL')
  , [detail?.replies]);
  const liReplies = useMemo(() =>
    (detail?.replies || []).filter((r) => r.channel?.startsWith('LINKEDIN'))
  , [detail?.replies]);

  // Default tab: whichever channel actually has a reply, prefer Email
  useEffect(() => {
    setTab(emailReplies.length === 0 && liReplies.length > 0 ? 1 : 0);
  }, [emailReplies.length, liReplies.length]);

  if (!detail) return (
    <Box sx={{
      flex: 1, minWidth: 0,
      bgcolor: 'background.paper',
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Replies</Typography>
      </Box>
      <Box sx={{ flex: 1, p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="caption">Select a company to see replies.</Typography>
      </Box>
    </Box>
  );

  const emailThreadLen = (detail?.email_thread || []).length;
  const e = detail;
  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      bgcolor: 'background.paper',
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Conversation · {e.company_name}
          </Typography>
        </Box>

        {/* Resume-outreach banner — only when the enrollment was reply-stopped */}
        {e.status === 'STOPPED_REPLIED' && (
          <Box sx={{
            px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider',
            bgcolor: resumeStage === 'idle' ? 'warning.lighter'
                   : resumeStage === 'first' ? 'info.lighter'
                   : 'error.lighter',
          }}>
            {resumeStage === 'idle' && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                    Outreach paused — a reply was received
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The sequence stopped because someone replied
                    (could be the contact, or an auto-responder).
                    Resume only if you've confirmed it was an auto-reply.
                  </Typography>
                </Box>
                <Button
                  size="small" variant="outlined" color="warning"
                  onClick={() => setResumeStage('first')}
                  disabled={resuming}
                >
                  Continue auto outreach
                </Button>
              </Stack>
            )}
            {resumeStage === 'first' && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.dark' }}>
                    Are you sure?
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The remaining steps will fire at their original delays.
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setResumeStage('idle')} disabled={resuming}>
                  Cancel
                </Button>
                <Button
                  size="small" variant="contained" color="info"
                  onClick={() => setResumeStage('second')}
                  disabled={resuming}
                >
                  Yes, continue
                </Button>
              </Stack>
            )}
            {resumeStage === 'second' && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.dark' }}>
                    Final confirmation — this contact will get more outreach
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Make sure you've checked the reply carefully. Click to resume now.
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setResumeStage('idle')} disabled={resuming}>
                  Cancel
                </Button>
                <Button
                  size="small" variant="contained" color="error"
                  startIcon={resuming ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={handleResume}
                  disabled={resuming}
                >
                  Resume sequence
                </Button>
              </Stack>
            )}
          </Box>
        )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2,
                  minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}>
        <Tab iconPosition="start" sx={{ textTransform: 'none' }}
             icon={<Icon icon={emailFill} width={15} />}
             label={`Email${emailThreadLen ? ` · ${emailThreadLen}` : ''}`} />
        <Tab iconPosition="start" sx={{ textTransform: 'none' }}
             icon={<Icon icon={linkedinFill} width={15} color="#0A66C2" />}
             label={`LinkedIn${liReplies.length ? ` · ${liReplies.length}` : ''}`} />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {tab === 0 && (
          <EmailThreadView
            detail={detail}
            emailReplies={emailReplies}
            busy={busy}
            onSend={onSend}
          />
        )}
        {tab === 1 && (liReplies.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 1, opacity: 0.4 }}>
              <Icon icon={linkedinFill} width={32} color="#0A66C2" />
            </Box>
            <Typography variant="caption" color="text.secondary">
              No LinkedIn reply yet.
            </Typography>
          </Box>
        ) : liReplies.map((r) => (
          <React.Fragment key={r.id}>
            <ReplyView reply={r} busy={busy} onSend={onSend} />
            <Divider />
          </React.Fragment>
        )))}
      </Box>
    </Box>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
const MyOutreachHome = ({ initialPmId, campaign, onBack }) => {
  const userInfo = UserInfo();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState(null);
  const [replies, setReplies]     = useState([]);   // for blink + sort
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [snack, setSnack]         = useState(null);
  // Count of companies waiting for this BA's review (across all campaigns)
  const [reviewPending, setReviewPending] = useState(0);

  // --- Campaign-mode pagination state ---
  // statusKey drives the filter chip + the ?status= query.
  // statusCounts always reflect the full DB (one shot per pm/campaign change).
  // nextOffset is the offset of the *next* page; null when fully loaded.
  const [statusKey, setStatusKey] = useState('all');
  const [statusCounts, setStatusCounts] = useState({ all: 0, replied: 0, active: 0, invalid: 0, done: 0 });
  const [totalForFilter, setTotalForFilter] = useState(0);
  const [nextOffset, setNextOffset]         = useState(null);
  const [loadingMore, setLoadingMore]       = useState(false);
  const PAGE_LIMIT = 50;

  // Load review-queue summary on mount + every 60s (only when not in embedded campaign view)
  useEffect(() => {
    if (campaign) return undefined;
    const fetch = () => getMyReviewQueue()
      .then((r) => {
        const total = (r.data?.sections || []).reduce(
          (acc, s) => acc + (s.results?.length || 0), 0
        );
        setReviewPending(total);
      })
      .catch(() => setReviewPending(0));
    fetch();
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, [campaign]);

  // Determine selected BA: campaign context => initialPmId (prop) => logged-in user's id
  const pmUserIds = campaign?.pm_assignments?.map((p) => p.user_id) || [];
  const defaultPmId = (() => {
    if (initialPmId) return Number(initialPmId);
    // Admin campaign-conversations view: show ALL BAs' conversations. The
    // legacy pm_assignments is empty after the sender/reviewer decouple, so we
    // must NOT fall back to the logged-in admin (who owns no enrollments) —
    // that left the list empty. null = no pm_id filter = every enrollment.
    if (campaign) return null;
    if (pmUserIds.length) return pmUserIds[0];
    return userInfo?.pk ?? null;
  })();

  const [selectedPmId, setSelectedPmId] = useState(defaultPmId);

  // When campaign or initialPmId changes, reset selection + reload
  useEffect(() => {
    setSelectedPmId(defaultPmId);
  }, [campaign?.id, initialPmId, defaultPmId]);

  // Page-1 fetch + counts. Both campaign and standalone BA modes now use the
  // same paginated/counts API surface — only the endpoint changes.
  const fetchPage = (offset) => campaign
    ? getCampaignEnrollmentsForPM(campaign.id, selectedPmId, { limit: PAGE_LIMIT, offset, statusKey })
    : getMyCompanies(selectedPmId, { limit: PAGE_LIMIT, offset, statusKey });

  const fetchCounts = () => campaign
    ? getCampaignEnrollmentStatusCounts(campaign.id, selectedPmId)
    : getMyCompaniesStatusCounts(selectedPmId);

  const loadList = async () => {
    const pmId = selectedPmId;
    // Campaign view: fetch ALL replies for the campaign (sender/reviewer are
    // decoupled, so pm-scoping hides them from an admin). Standalone BA view:
    // the BA's own replies.
    const [enrollmentsRes, repliesRes, countsRes] = await Promise.all([
      fetchPage(0),
      campaign ? getMyReplies(null, { campaignId: campaign.id }) : getMyReplies(pmId),
      fetchCounts(),
    ]);
    const page = enrollmentsRes.data || {};
    const cos = page.results || [];
    const loaded = cos.length;
    const total  = typeof page.count === 'number' ? page.count : loaded;
    setTotalForFilter(total);
    setNextOffset(loaded < total ? loaded : null);
    setStatusCounts(countsRes.data || {});

    // In campaign mode the backend already scopes replies to this campaign, so
    // surface them all — do NOT intersect with the (paginated) loaded enrollment
    // set, or replies whose enrollment sits on a later page get dropped.
    // Standalone mode: the BA sees their full reply inbox.
    const rps = repliesRes.data || [];
    setCompanies(cos);
    setReplies(rps);
    return cos;
  };

  const loadMore = async () => {
    if (nextOffset == null || loadingMore) return;
    const offsetSnapshot = nextOffset;
    setLoadingMore(true);
    try {
      const { data: page } = await fetchPage(offsetSnapshot);
      const more  = page?.results || [];
      const total = typeof page?.count === 'number' ? page.count : (offsetSnapshot + more.length);
      const newOffset = offsetSnapshot + more.length;
      setCompanies((prev) => {
        const seen = new Set((prev || []).map((c) => c.id));
        return [...(prev || []), ...more.filter((c) => !seen.has(c.id))];
      });
      setTotalForFilter(total);
      setNextOffset(newOffset < total ? newOffset : null);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadDetail = (id) => {
    if (!id) return;
    setDetailLoading(true);
    getMyCompanyDetail(id).then(({ data }) => {
      setDetail(data);
      setDetailLoading(false);
    });
  };

  // Reload list whenever BA, campaign, or status filter changes. statusKey is
  // only consulted in campaign mode; harmless to include for non-campaign mode.
  useEffect(() => {
    // Reset list-related state for a clean fetch.
    setCompanies(null);
    setNextOffset(null);
    setLoadingMore(false);
    loadList();
  }, [selectedPmId, campaign?.id, statusKey]);

  // Reset the status filter back to 'all' when BA or campaign changes.
  useEffect(() => {
    setStatusKey('all');
  }, [selectedPmId, campaign?.id]);

  // Auto-sort: unread replies (need attention) pinned on top, then everything
  // by most-recent activity. Mirrors the server order so loaded pages refine
  // cleanly. Uses the live replies state (same signal as the blink) so a
  // company drops out of the pinned group the moment its reply is read.
  const sortedCompanies = useMemo(() => {
    if (!companies) return null;
    const withUnread = (id) => replies.some((r) => r.enrollment === id && r.is_unread);
    return [...companies].sort((a, b) => {
      const aU = withUnread(a.id), bU = withUnread(b.id);
      if (aU !== bU) return bU - aU;
      return new Date(b.last_activity_at) - new Date(a.last_activity_at);
    });
  }, [companies, replies]);

  // Auto-select first company on load (or when BA/campaign changes)
  useEffect(() => {
    if (selectedId || !sortedCompanies?.length) return;
    setSelectedId(sortedCompanies[0].id);
  }, [sortedCompanies, selectedId]);

  // Reset selected when BA changes or companies refresh
  useEffect(() => {
    setSelectedId(null);
    setDetail(null);
  }, [selectedPmId, campaign?.id]);

  // Load detail + auto-mark-as-read when selection changes
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    loadDetail(selectedId);
    const unreadHere = replies.filter((r) => r.enrollment === selectedId && r.is_unread);
    if (unreadHere.length) {
      Promise.all(unreadHere.map((r) => markReplyRead(r.id))).then(() => {
        setReplies((all) => all.map((r) =>
          r.enrollment === selectedId ? { ...r, is_unread: false } : r
        ));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const onSend = async (replyId, body, { takeOver }) => {
    setBusy(true);
    try {
      await replyToThread(replyId, body, { takeOver });
      setSnack({
        severity: 'success',
        message: takeOver ? 'Reply sent. Sequence stopped for this company.' : 'Reply sent. Automation continues.',
      });
      await loadList();
      loadDetail(selectedId);
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err?.response?.data?.detail || 'Failed to send reply.',
      });
    } finally {
      setBusy(false);
    }
  };
  const onResumeEnrollment = async (enrollmentId) => {
    setBusy(true);
    try {
      const { data } = await resumeEnrollment(enrollmentId);
      setSnack({
        severity: 'success',
        message: `Sequence resumed — next step at ${new Date(data.scheduled_at).toLocaleString()}.`,
      });
      await loadList();
      loadDetail(enrollmentId);
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err?.response?.data?.detail || 'Failed to resume sequence.',
      });
    } finally {
      setBusy(false);
    }
  };
  const onMarkInvalid = async (enrollmentId, { reason_id, description }) => {
    setBusy(true);
    const { data } = await markCompanyInvalid(enrollmentId, { reason_id, description });
    setBusy(false);
    setSnack({
      severity: 'warning',
      message: `Marked invalid · ${data.label}.`,
    });
    await loadList();
    loadDetail(selectedId);
  };
  const onMoveStage = async (enrollmentId, stage, options) => {
    setBusy(true);
    await moveCompanyStage(enrollmentId, stage, options);
    setBusy(false);
    setSnack({
      severity: 'success',
      message: `Moved to ${stageLabelFor(stage) || stage}${options?.newsletter_only ? ' (newsletter only)' : ''}.`,
    });
    await loadList();
    loadDetail(selectedId);
  };

  if (!sortedCompanies) return (
    <Page title="My Outreach | CRM">
      <Box sx={{ p: 4 }}><CircularProgress /></Box>
    </Page>
  );

  const showPmSelector = !!campaign && pmUserIds.length > 0; // show when viewing a campaign with BA assignments

  return (
    <Page
      title={campaign ? `${campaign.name} | CRM` : "My Outreach | CRM"}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: campaign ? '100%' : 'calc(100vh - 24px)',
        minHeight: 0,
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.default',
      }}>
        {/* ── Header with BA selector (if applicable) ── */}
        {showPmSelector && (
          <Box sx={{
            px: 2, py: 1.25,
            borderBottom: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', gap: 2,
            flexShrink: 0,
          }}>
            <Box sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id={`pm-select-label-${campaign?.id || 'global'}`}>Process Manager</InputLabel>
              <Select
                labelId={`pm-select-label-${campaign?.id || 'global'}`}
                value={selectedPmId}
                label="Process Manager"
                onChange={(e) => setSelectedPmId(Number(e.target.value))}
              >
                {pmUserIds.map((pmId) => {
                  const pm = campaign?.pm_assignments?.find((p) => p.user_id === pmId);
                  const name = pm?.name || `BA ${pmId}`;
                  return (
                    <MenuItem key={pmId} value={pmId}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: 'primary.main' }}>
                          {(name || '?').split(' ').slice(0,2).map(s=>s[0]).join('')}
                        </Avatar>
                        <Typography variant="body2">{name}</Typography>
                        {pm?.outlook_connected && <Icon icon={emailFill} width={12} color="#1976d2" />}
                        {pm?.unipile_connected && <Icon icon={linkedinFill} width={12} color="#0A66C2" />}
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ── Daily review banner — only on standalone /my-outreach view ── */}
        {!campaign && reviewPending > 0 && (
          <Box
            onClick={() => navigate('/my-outreach/review')}
            sx={{
              px: 2.5, py: 1.25, flexShrink: 0,
              borderBottom: 1, borderColor: 'warning.light',
              bgcolor: 'warning.lighter',
              display: 'flex', alignItems: 'center', gap: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'warning.light' },
            }}
          >
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%',
              bgcolor: 'warning.dark', color: 'common.white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '0.85rem', flexShrink: 0,
            }}>
              {reviewPending}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                {reviewPending} compan{reviewPending === 1 ? 'y' : 'ies'} waiting for your review
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Approve or reject before they enter the outreach sequence.
              </Typography>
            </Box>
            <Button size="small" variant="contained" color="warning">
              Review now
            </Button>
          </Box>
        )}

        {/* ── Three-pane layout ── fills remaining space */}
        <Box sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}>
          <CompanyListPane
            companies={sortedCompanies}
            replies={replies}
            selectedId={selectedId}
            onSelect={setSelectedId}
            title={campaign ? campaign.name : undefined}
            statusKey={statusKey}
            onStatusKeyChange={setStatusKey}
            counts={statusCounts}
            totalForCurrentFilter={totalForFilter}
            hasMore={nextOffset != null}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            clientSideFilter={false}
          />
          <RepliesPane
            detail={detail}
            busy={busy}
            onSend={onSend}
            onResume={onResumeEnrollment} />
          <DetailsPane detail={detail} isLoading={detailLoading} busy={busy}
                       onMoveStage={onMoveStage}
                       onMarkInvalid={onMarkInvalid}
                       onSendNow={() => selectedId && loadDetail(selectedId)} />
        </Box>

        <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
          {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
        </Snackbar>
      </Box>
    </Page>
  );
};

export default MyOutreachHome;
